import { useLanguage, type Language, type LanguageLocale } from "./language.js";

/**
 * The lookup: an identifier in, a sentence in the reader's language out.
 *
 * ==========================================================================================
 * WHY `Intl` AND NOT AN i18n LIBRARY
 * ==========================================================================================
 *
 * The constitution's technology list is settled, so adding one would be an amendment. It would buy
 * ICU MessageFormat, forty-odd plural categories, namespaces and a loader — and this application has
 * two languages, each with exactly the two plural categories `Intl.PluralRules` reports for `nl` and
 * `en`. `Intl` is already the formatting substrate here: `Intl.NumberFormat` in `money.ts`,
 * `Intl.DateTimeFormat` in `format.ts` beside this file.
 *
 * Nothing below is arity-two. A `{ one, other }` record widens to the full category set by gaining
 * fields, without changing a single call site, so a third language is not foreclosed.
 *
 * ==========================================================================================
 * THE ACTIVE CATALOGUE IS AMBIENT MODULE STATE, FOR `money.ts`'s REASON
 * ==========================================================================================
 *
 * `formatEur` made this trade first and wrote down why: a second parameter means threading a value
 * through every call site and every function that wraps one, and a caller who forgets it silently
 * gets the wrong answer. The same applies here with more surface — roughly 150 components.
 *
 * What keeps it honest is that it is write-once-at-boot in practice: `apps/app/src/main.tsx` loads
 * one catalogue before the first render, and `setActiveCatalogue` is called again only when the
 * reader changes language, which re-renders everything anyway.
 */

/** One piece of interface text. A record when the wording depends on a count. */
export type CatalogueEntry = string | { readonly one: string; readonly other: string };

/** Every identifier the interface can ask for. Both language modules satisfy this exactly. */
export type Catalogue = Readonly<Record<string, CatalogueEntry>>;

export type TranslateParams = Readonly<Record<string, string | number>>;

export type TranslateFn = (id: string, params?: TranslateParams) => string;

/**
 * THERE IS NO CROSS-LANGUAGE FALLBACK, AND THAT IS A CORRECTION TO THIS FEATURE'S OWN PLAN.
 *
 * The spec's Assumptions say a missing Dutch string should fall back to the English one "so a gap
 * degrades to a readable screen". Implementing that means every Dutch reader downloads the ENTIRE
 * English catalogue in order to insure against a gap — and FR-043 says in as many words that the
 * other language's strings are never delivered to a reader who is not reading them. A numbered
 * requirement outranks an assumption, and the assumption was buying very little: `catalogue-parity`
 * asserts key equality in both directions, so the gap it insures against fails the build.
 *
 * A missing key therefore renders as the identifier. That is visibly wrong, it names the key that is
 * missing, and it is not a blank space that reads as a rendering bug.
 */
let activeCatalogue: Catalogue = {};
let activeLanguage: Language = "en";
let activeLocaleValue: LanguageLocale = "en-GB";

const PLURAL_RULES = new Map<string, Intl.PluralRules>();

function pluralRules(locale: LanguageLocale): Intl.PluralRules {
  const cached = PLURAL_RULES.get(locale);
  if (cached !== undefined) return cached;
  const made = new Intl.PluralRules(locale);
  PLURAL_RULES.set(locale, made);
  return made;
}

/** Installs a catalogue as the one every later `t` reads. */
export function setActiveCatalogue(
  language: Language,
  locale: LanguageLocale,
  catalogue: Catalogue,
): void {
  activeLanguage = language;
  activeLocaleValue = locale;
  activeCatalogue = catalogue;
}

/** The language currently installed. For tests, and for anything that reports state. */
export function activeCatalogueLanguage(): Language {
  return activeLanguage;
}

/**
 * The locale every figure and every date is rendered in.
 *
 * ONE SOURCE, AND IT IS THIS ONE. `format.ts` used to re-derive it from the store —
 * `resolveLanguage(readLanguageChoice()).locale` — while plural selection read the value installed
 * here. Two derivations of one fact, which is the exact defect this feature exists to remove, and it
 * showed up the moment a unit test installed a catalogue and got Dutch dates beside English words.
 *
 * `setActiveCatalogue` is the only writer, and `loadCatalogue` and `changeLanguage` are the only
 * callers of that — so the words, the plurals, the amounts and the dates cannot disagree.
 */
export function activeLocale(): LanguageLocale {
  return activeLocaleValue;
}

/**
 * Substitutes `{name}` placeholders. Unknown placeholders are LEFT AS WRITTEN rather than blanked,
 * because a visible `{count}` on screen is a bug report and an empty gap is a mystery.
 */
function interpolate(text: string, params: TranslateParams | undefined): string {
  if (params === undefined) return text;
  return text.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = params[key];
    return value === undefined ? whole : String(value);
  });
}

function select(entry: CatalogueEntry, params: TranslateParams | undefined): string {
  if (typeof entry === "string") return entry;

  /*
   * A record entry with no count is a call site that forgot one. `other` is the right guess — it is
   * the form that reads correctly for every number except one — and the parity test cannot catch
   * this, because both forms exist. It is a wrong sentence rather than a missing one.
   */
  const count = params?.["count"];
  if (typeof count !== "number") return entry.other;

  return pluralRules(activeLocaleValue).select(count) === "one" ? entry.one : entry.other;
}

/**
 * The lookup itself, outside React.
 *
 * Non-component callers need it — a chart's tick formatter, a column definition, a sort comparator —
 * for exactly the reason `formatEur` is not a hook.
 */
export function t(id: string, params?: TranslateParams): string {
  const entry = activeCatalogue[id];

  // See the note on `activeCatalogue`: unreachable while `catalogue-parity` passes, and the
  // identifier is the least-bad reachable answer if it ever does not.
  if (entry === undefined) return id;

  return interpolate(select(entry, params), params);
}

/**
 * The lookup for components, re-rendering them when the language changes.
 *
 * It returns `t` itself rather than a fresh closure: `t` reads module state, so it needs no identity
 * of its own, and a stable function reference keeps it usable in a dependency array.
 */
export function useT(): TranslateFn {
  // Subscribes this component to the language store. The value is deliberately unused — what matters
  // is that a language change re-renders every component that translates anything.
  useLanguage();
  return t;
}
